import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button, Card, Row, Col, Alert, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "../../components/Common/Bredcrump";
import CommentBox from "../../components/Common/CommentBox";
import { commentService } from "../../services/commentService";
import { useAuth } from "../../components/Context/authContext";
import { getFreeFormContentById } from "../../services/freeFormContentService";
import { LiaCommentDots } from "react-icons/lia";
import "./FreeFormContent.css";

// Function to render metafield value as HTML
const renderMetafieldValue = (value) => {
  if (!value) return 'N/A';
  
  // If it's a string, try to parse as JSON first
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return renderRichTextContent(parsed);
    } catch (e) {
      // If not JSON, return as plain text
      return value;
    }
  }
  
  // If it's already an object, render it
  if (typeof value === 'object') {
    return renderRichTextContent(value);
  }
  
  return value;
};

// Function to render rich text content (similar to StreamingContentRenderer)
const renderRichTextContent = (content) => {
  if (!content) return '';
  
  if (typeof content === 'string') {
    return content;
  }
  
  if (content.type === 'root' && content.children) {
    return (
      <div>
        {content.children.map((child, index) => renderNode(child, index))}
      </div>
    );
  }
  
  return JSON.stringify(content);
};

// Function to render individual nodes
const renderNode = (node, index) => {
  if (!node) return null;
  
  const key = `node-${index}`;
  
  switch (node.type) {
    case 'heading':
      const HeadingTag = `h${node.level || 2}`;
      return (
        <HeadingTag key={key} style={{ 
          fontWeight: 'bold', 
          marginBottom: '16px',
          marginTop: '24px',
          fontSize: node.level === 1 ? '24px' : node.level === 2 ? '20px' : '18px'
        }}>
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </HeadingTag>
      );
      
    case 'paragraph':
      return (
        <p key={key} style={{ marginBottom: '16px', lineHeight: '1.6' }}>
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </p>
      );
      
    case 'text':
      const textStyle = {};
      if (node.bold) textStyle.fontWeight = 'bold';
      if (node.italic) textStyle.fontStyle = 'italic';
      if (node.underline) textStyle.textDecoration = 'underline';
      
      return (
        <span key={key} style={textStyle}>
          {node.value}
        </span>
      );
      
    case 'link':
      return (
        <a 
          key={key} 
          href={node.url} 
          target={node.target || '_blank'}
          rel="noopener noreferrer"
          style={{ color: '#007bff', textDecoration: 'underline' }}
        >
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </a>
      );
      
    case 'list':
      const ListTag = node.format === 'ordered' ? 'ol' : 'ul';
      return (
        <ListTag key={key} style={{ marginBottom: '16px', paddingLeft: '20px' }}>
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </ListTag>
      );
      
    case 'listItem':
      return (
        <li key={key} style={{ marginBottom: '8px' }}>
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </li>
      );
      
    case 'blockquote':
      return (
        <blockquote key={key} style={{ 
          borderLeft: '4px solid #ddd', 
          paddingLeft: '16px', 
          margin: '16px 0',
          fontStyle: 'italic',
          color: '#666'
        }}>
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </blockquote>
      );
      
    default:
      return (
        <div key={key}>
          {node.children?.map((child, childIndex) => renderNode(child, childIndex))}
        </div>
      );
  }
};

const PreviewFreeFormContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contentId } = useParams();
  const { user: currentUser } = useAuth();
  const contentData = location.state?.contentData;
  const source = location.state?.source; // Track where we came from


  const [viewMode, setViewMode] = useState(location.state?.viewMode || "desktop");

  // Comment system state
  const previewRef = useRef(null);
  const [mouseY, setMouseY] = useState(window.innerHeight / 2);
  const [insidePreview, setInsidePreview] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [comments, setComments] = useState([]);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentPosition, setCommentPosition] = useState(null);


  const [content, setContent] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load content by ID or from state - Following BlogPreview pattern exactly
  useEffect(() => {
    const loadContent = async () => {
      if (contentData) {
        // Use contentData from state (when navigating from edit/create)
        setContent(contentData);
        setLoading(false);
        // Load comments if we have an ID
        if (contentData.id) {
          loadComments();
        }
      } else if (contentId) {
        // Load content by ID (fallback for direct URL access) - Following BlogPreview pattern
        setError(null); // Clear any previous errors
        try {
          const response = await getFreeFormContentById(contentId);
          if (response.success) {
            setContent(response.data);
            setLoading(false);
            // Load comments after content is loaded
            loadComments();
          } else {
            setError(response.message || 'Failed to load content');
            setLoading(false);
          }
        } catch (error) {
          console.error('Error loading content:', error);
          setError('Failed to load content');
          setLoading(false);
        }
      } else {
        // No contentId and no contentData - this shouldn't happen but handle gracefully
        setError('No content to display');
        setLoading(false);
      }
    };

    loadContent();
  }, [contentId, contentData]);

  const handleEdit = () => {
    navigate('/skills/free-form-content/edit', { 
      state: { contentData: content } 
    });
  };

  const handleBack = () => {
    // Check the source to determine where to go back
    if (source === 'edit') {
      // If we came from edit mode, go back to edit page with complete data
      const editData = {
        ...contentData, // Start with original data
        ...content, // Override with any changes from preview
        // Preserve original prompt field (don't use the modified one from preview)
        prompt: contentData?.prompt || "",
        // Ensure we have all the fields that edit page expects
        generatedContent: content?.generatedContent || content?.generated_content,
        generated_content: content?.generatedContent || content?.generated_content,
        metafields: content?.metafields || contentData?.metafields || [],
        tags: content?.tags || contentData?.tags || "",
        uid: content?.uid || contentData?.uid || contentData?.id || ""
      };
      
      navigate('/skills/free-form-content/edit', { 
        state: { contentData: editData } 
      });
    } else if (source === 'create') {
      // If we came from create mode, go back to create page
      navigate('/skills/free-form-content/create', { 
        state: { contentData: content } 
      });
    } else {
      // Otherwise, go back to the list page
    navigate('/skills/free-form-content');
    }
  };

  // Load comments from backend
  const loadComments = async () => {
    const id = contentId || contentData?.id;
    if (!id) return;
    
    try {
      const response = await commentService.getComments('Free_Form_Content', id);
      const formattedComments = response.data.map(comment => ({
        id: comment.id,
        text: comment.content,
        position: comment.position_y,
        isSaved: true,
        user: comment.user,
        createdAt: comment.formatted_created_at
      }));
      setComments(formattedComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
      // Don't show error toast for comments as it's not critical
    }
  };

  // Comment system handlers
  const handleCommentClick = () => {
    // Allow opening comment box for anonymous users too
    setCommentPosition(mouseY);
    setShowCommentBox(true);
  };

  const handleSaveComment = async (text) => {
    const id = contentId || contentData?.id;
    if (!text.trim() || !id) return;
    try {
      const commentData = {
        content: text.trim(),
        position_x: 0,
        position_y: commentPosition,
      };
      const response = await commentService.createComment('Free_Form_Content', id, commentData);
      const created = response.data;
      setComments((prev) => [
        ...prev,
        {
          id: created.id,
          text: created.content,
          position: created.position_y,
          isSaved: true,
          user: created.user, // may be null
          createdAt: created.formatted_created_at
        },
      ]);
      setShowCommentBox(false);
    } catch (err) {
      console.error('Failed to save comment', err);
      toast.error('Failed to save comment. Please try again.');
    }
  };

  const handleEditComment = async (index, newText) => {
    const comment = comments[index];
    const id = contentId || contentData?.id;
    if (!comment.id || !id) {
      // Handle local comments that haven't been saved yet
      const updatedComments = [...comments];
      updatedComments[index].text = newText;
      setComments(updatedComments);
      return;
    }
    
    try {
      const commentData = {
        content: newText.trim()
      };
      
      const response = await commentService.updateComment('Free_Form_Content', id, comment.id, commentData);
      const updatedComments = [...comments];
      updatedComments[index] = {
        ...updatedComments[index],
        text: response.data.content,
        createdAt: response.data.formatted_created_at
      };
      setComments(updatedComments);
      toast.success('Comment updated successfully!');
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast.error('Failed to update comment. Please try again.');
    }
  };

  const handleDeleteComment = async (index) => {
    const comment = comments[index];
    const id = contentId || contentData?.id;
    if (!comment.id || !id) {
      // Handle local comments that haven't been saved yet
      setComments(comments.filter((_, i) => i !== index));
      return;
    }
    
    try {
      await commentService.deleteComment('Free_Form_Content', id, comment.id);
      setComments(comments.filter((_, i) => i !== index));
      toast.success('Comment deleted successfully!');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment. Please try again.');
    }
  };

  // Mouse tracking for comment positioning
  useEffect(() => {
    const handleMouseMove = (e) => {
      const previewContainer = previewRef.current;
      if (previewContainer) {
        const { top, height } = previewContainer.getBoundingClientRect();
        let relativeY = e.clientY - top;
        let clampedY = Math.max(50, Math.min(relativeY, height - 50));
        setMouseY(clampedY);
      }
    };

    const handleMouseEnter = () => setInsidePreview(true);
    const handleMouseLeave = () => setInsidePreview(false);

    document.addEventListener("mousemove", handleMouseMove);
    const previewContainer = previewRef.current;
    if (previewContainer) {
      previewContainer.addEventListener("mouseenter", handleMouseEnter);
      previewContainer.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (previewContainer) {
        previewContainer.removeEventListener("mouseenter", handleMouseEnter);
        previewContainer.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);


  const renderContent = () => {
    // Following BlogPreview pattern - use content directly
    const generatedContent = content?.generated_content || content?.generatedContent || "";
    
    if (!generatedContent) {
      return (
        <div className="no-content" style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          padding: '40px 20px' 
        }}>
          <p>No content generated yet.</p>
          <small className="text-muted" style={{ color: '#9ca3af' }}>This content hasn't been generated or saved yet.</small>
        </div>
      );
    }

    // Clean and process content before displaying (same logic as StreamingContentRenderer)
    const cleanContent = (content) => {
      if (!content || typeof content !== 'string') return content;
      
      // Remove raw JSON artifacts and clean up content
      let cleaned = content
        .replace(/^\{[\s\S]*?"content":\s*"/, '') // Remove JSON prefix
        .replace(/"\s*\}\s*$/, '') // Remove JSON suffix
        .replace(/",\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact
        .replace(/,\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact (without quotes)
        .replace(/",\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere
        .replace(/,\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere (without quotes)
        .replace(/\\n/g, '\n') // Convert \n to actual newlines
        .replace(/\\"/g, '"') // Convert \" to actual quotes
        .replace(/\\t/g, '\t') // Convert \t to actual tabs
        .replace(/\s+$/, '') // Remove trailing whitespace
        .trim();
      
      // If it looks like HTML, return as-is
      if (/<[^>]+>/.test(cleaned)) {
        return cleaned;
      }
      
      // If it's plain text, wrap in paragraphs
      if (cleaned && !cleaned.includes('<')) {
        return cleaned.split('\n\n').map(paragraph => 
          paragraph.trim() ? `<p>${paragraph.trim()}</p>` : ''
        ).join('');
      }
      
      return cleaned;
    };

    const cleanedContent = cleanContent(generatedContent);

    // Render HTML content directly
        return (
            <div 
              className="generated-html-content"
        dangerouslySetInnerHTML={{ __html: cleanedContent }}
              style={{ 
                maxWidth: '100%', 
                overflowX: 'auto', 
                margin: '0', 
                padding: '0' 
              }}
            />
    );
  };

  if (loading) {
    return (
      <div>
        <Breadcrumb />
        <div className="text-center mt-5" style={{ 
          textAlign: 'center', 
          marginTop: '3rem', 
          padding: '3rem 1rem' 
        }}>
          <Spinner animation="border" role="status" style={{ color: '#f87171' }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2" style={{ marginTop: '0.5rem' }}>Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Breadcrumb />
        <Alert variant="danger" className="mt-3" style={{ 
          margin: '1rem 0',
          borderRadius: '0.5rem',
          border: 'none',
          backgroundColor: '#fef2f2',
          color: '#dc2626',
          borderLeft: '4px solid #dc2626'
        }}>
          <strong>Error:</strong> {error}
          <div className="mt-2">
            <Button onClick={handleBack} variant="outline-danger" style={{ 
              borderColor: '#dc2626',
              color: '#dc2626',
              backgroundColor: 'transparent',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem'
            }}>
              Back to Free Form Content
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!content) {
    return (
      <div>
        <Breadcrumb />
        <div className="no-content-data" style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          color: '#6b7280' 
        }}>
          <h3 style={{ marginBottom: '16px', color: '#374151' }}>No Content Data</h3>
          <p>No content data was provided for preview.</p>
          <p className="text-muted" style={{ color: '#9ca3af' }}>
            This usually happens when you navigate directly to the preview URL without selecting content first.
          </p>
          <div className="mt-3">
            <Button onClick={handleBack} variant="primary" className="me-2" style={{ 
              marginRight: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              backgroundColor: '#0d6efd',
              borderColor: '#0d6efd',
              color: 'white'
            }}>
              Back to Free Form Content
            </Button>
            <Button 
              onClick={() => navigate('/skills/free-form-content')} 
              variant="outline-primary"
              style={{ 
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                backgroundColor: 'transparent',
                borderColor: '#0d6efd',
                color: '#0d6efd'
              }}
            >
              Go to Content List
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="free-form-preview-main">
      <ToastContainer />
      {/* Top Bar */}
      <div className="preview-header">
        <div
          className="preview-header-back"
          onClick={handleBack}
        >
          &lt; Back
        </div>
        <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
          <div className="d-flex justify-content-center align-items-center">
            <button
              className={`btn ${viewMode === "desktop" ? "active" : ""}`}
              onClick={() => {
              
                setViewMode("desktop");
              }}
             
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20 17.722c.595-.347 1-.985 1-1.722V5c0-1.103-.897-2-2-2H5c-1.103 0-2 .897-2 2v11c0 .736.405 1.375 1 1.722V18H2v2h20v-2h-2v-.278zM5 16V5h14l.002 11H5z"></path>
              </svg>
            </button>
            |
            <button
              className={`btn ${viewMode === "mobile" ? "active" : ""}`}
              onClick={() => {
                
                setViewMode("mobile");
              }}
             
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17 2H7c-1.103 0-2 .897-2 2v16c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM7 16.999V5h10l.002 11.999H7z"></path>
              </svg>
            </button>
          </div>
        </div>
        <div className="btns d-flex justify-content-end gap-3">
          {!content?.is_shopify_page && (
            <div>
              <button
                className="btn bgprimary rounded"
                onClick={() => {
                  const shareUrl = contentId 
                    ? `${window.location.origin}/skills/free-form-content/preview/${contentId}`
                    : window.location.href;
                  navigator.clipboard.writeText(shareUrl);
                  toast.success("Link copied to clipboard!");
                }}
              >
                Share
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content with responsive container based on viewMode */}
      <div className="d-flex justify-content-center p-4">
        <div
          ref={previewRef}
          className={`free-form-content free-form-content-container ${viewMode}`}
          style={{
            width: viewMode === "mobile" ? "375px" : "100%",
            maxWidth: viewMode === "desktop" ? "1000px" : "375px",
            border: viewMode === "mobile" ? "12px solid #333" : "none",
            borderRadius: viewMode === "mobile" ? "30px" : "0",
            height: viewMode === "mobile" ? "667px" : "auto",
            overflow: viewMode === "mobile" ? "hidden" : "visible",
            backgroundColor: "white",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            transition: "all 0.3s ease",
            padding: "20px",
            position: "relative",
          }}
          data-viewmode={viewMode}
        >
          {/* Comment System */}
          {!showCommentBox && (
            <div
              style={{
                position: "absolute",
                right: "-60px",
                top: `${mouseY}px`,
                zIndex: 1000,
                cursor: "pointer",
                fontSize: "24px",
                color: isHovered ? "#D04734" : "#666",
                backgroundColor: isHovered ? "rgba(208, 71, 52, 0.1)" : "rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isHovered ? "0 4px 12px rgba(208, 71, 52, 0.3)" : "0 2px 8px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
                animation: isHovered ? "heartbeat 1.5s ease-in-out infinite" : "none",
                border: isHovered ? "2px solid #D04734" : "1px solid rgba(0,0,0,0.1)",
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={(e) => {
                e.stopPropagation();
                handleCommentClick();
              }}
              aria-label="Add comment"
              title={"Add comment"}
            >
              {isHovered ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 45 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M43.18 21.976C43.18 33.5312 33.708 42.952 22.09 42.952C17.576 42.952 13.358 41.5536 9.88 39.1248L1 42.952L4.848 34.12C2.406 30.7344 1 26.5392 1 21.976C1 10.4208 10.472 1 22.09 1C33.708 1 43.18 10.4208 43.18 21.976Z"
                    stroke="#D04734"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="15" cy="22" r="2" fill="#D04734" />
                  <circle cx="22" cy="22" r="2" fill="#D04734" />
                  <circle cx="29" cy="22" r="2" fill="#D04734" />
                </svg>
              ) : (
                <LiaCommentDots />
              )}
            </div>
          )}
          
          {/* Existing Comments */}
          {comments.map((comment, index) => (
            <CommentBox
              key={index}
              position={comment.position}
              commentText={comment.text}
              isSaved={comment.isSaved}
              onSave={(text) => handleEditComment(index, text)}
              onEdit={(newText) => handleEditComment(index, newText)}
              onDelete={() => handleDeleteComment(index)}
              user={comment.user}
              canModify={!!currentUser}
            />
          ))}

          {/* New Comment Box */}
          {showCommentBox && (
            <CommentBox
              position={commentPosition}
              onSave={handleSaveComment}
              onCancel={() => setShowCommentBox(false)}
              user={currentUser}
            />
          )}
          
          {/* Scrollable content container for mobile */}
          <div 
            style={{
              height: viewMode === "mobile" ? "calc(100% - 40px)" : "auto",
              overflowY: viewMode === "mobile" ? "auto" : "visible",
              overflowX: "hidden",
              paddingRight: viewMode === "mobile" ? "10px" : "0"
            }}
          >
            {/* Main Title */}
            {content?.title && (
            <h1
              style={{
                fontSize: viewMode === "mobile" ? 24 : 32,
                fontWeight: 700,
                color: "#111827",
                marginBottom: 32,
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              {content.title}
            </h1>
          )}

          {/* Content Information Section */}
          <div className="content-info-section" style={{
            marginBottom: 40,
            borderBottom: "1px solid #f3f4f6",
            paddingBottom: 32,
          }}>
            <div className="content-meta" style={{
              display: "grid",
              gridTemplateColumns: viewMode === "mobile" ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px"
            }}>
              {content?.language && (
                <div className="meta-item">
                  <label style={{ fontWeight: 600, color: "#374151", marginBottom: "8px", display: "block" }}>Language:</label>
                  <span style={{ 
                    color: "#667eea", 
                    fontWeight: "500", 
                    background: "#f0f4ff", 
                    padding: "4px 12px", 
                    borderRadius: "16px", 
                    fontSize: "12px" 
                  }}>{content.language}</span>
                </div>
              )}
              
              {content?.locationFocus && (
                <div className="meta-item">
                  <label style={{ fontWeight: 600, color: "#374151", marginBottom: "8px", display: "block" }}>Location Focus:</label>
                  <span style={{ 
                    color: "#667eea", 
                    fontWeight: "500", 
                    background: "#f0f4ff", 
                    padding: "4px 12px", 
                    borderRadius: "16px", 
                    fontSize: "12px" 
                  }}>{content.locationFocus}</span>
                </div>
              )}

              {content?.status && (
                <div className="meta-item">
                  <label style={{ fontWeight: 600, color: "#374151", marginBottom: "8px", display: "block" }}>Status:</label>
                  <span style={{ 
                    color: "#10b981", 
                    fontWeight: "500", 
                    background: "#ecfdf5", 
                    padding: "4px 12px", 
                    borderRadius: "16px", 
                    fontSize: "12px" 
                  }}>{content.status}</span>
                </div>
              )}

              {content?.created_at && (
                <div className="meta-item">
                  <label style={{ fontWeight: 600, color: "#374151", marginBottom: "8px", display: "block" }}>Created:</label>
                  <span style={{ 
                    color: "#6b7280", 
                    fontWeight: "500", 
                    background: "#f9fafb", 
                    padding: "4px 12px", 
                    borderRadius: "16px", 
                    fontSize: "12px" 
                  }}>{new Date(content.created_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>


          </div>

          {/* Content Section - Always show, render content if available */}
          <div className="content-section" style={{ marginTop: "32px" }} key={content?.generated_content || content?.generatedContent}>
            <div className="content-preview" style={{ 
              fontSize: viewMode === "mobile" ? 14 : 16,
              lineHeight: 1.6,
              color: "#374151"
            }}>
              {renderContent()}
            </div>
          </div>

          {/* Metafields Section - At the very end */}
          {content?.metafields && content.metafields.length > 0 && (
            <div className="metafields-section" style={{ marginTop: "48px", borderTop: "2px solid #f3f4f6", paddingTop: "32px" }}>
            <h3 style={{
              fontWeight: 600,
              fontSize: viewMode === "mobile" ? 18 : 22,
              marginBottom: 24,
              color: "#1f2937",
              borderLeft: "4px solid #E36250",
              paddingLeft: 16,
              lineHeight: 1.3,
            }}>
                Shopify Metafields
            </h3>
              <div className="metafields-container">
                {content.metafields.map((metafield, index) => (
                  <div key={metafield.key || index} className="metafield-item mb-3 p-3" style={{
                    background: '#f8f9fa',
                    borderRadius: '5px',
                    border: '1px solid #dee2e6'
                  }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                          Key: {metafield.key || 'N/A'}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                          Namespace: {metafield.namespace || 'N/A'}
                        </div>
                        {metafield.description && (
                          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                            Description: {metafield.description}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ 
                      background: '#ffffff', 
                      padding: '12px', 
                      borderRadius: '4px', 
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <strong>Value:</strong>
                      <div style={{ marginTop: '8px' }}>
                        {renderMetafieldValue(metafield.value)}
                      </div>
                    </div>
                    {metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0 && (
                      <div className="mt-3">
                        <h6 className="text-success mb-2">Metaobjects ({metafield.metaobjects.length} items)</h6>
                        <div className="row">
                          {metafield.metaobjects.map((metaobject, metaIndex) => (
                            <div key={metaobject.id || metaIndex} className="col-12 mb-3">
                              <div className="metaobject-card p-3" style={{
                                background: '#f8f9fa',
                                borderRadius: '5px',
                                border: '1px solid #dee2e6'
                              }}>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="flex-grow-1">
                                    <div style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                                      ID: {metaobject.id || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                                      Type: {metaobject.type || 'N/A'}
                                    </div>
                                    {metaobject.description && (
                                      <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                        Description: {metaobject.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {metaobject.fields && Array.isArray(metaobject.fields) && metaobject.fields.length > 0 ? (
                                  <div style={{ 
                                    background: '#ffffff', 
                                    padding: '12px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #e5e7eb',
                                    fontSize: '14px',
                                    color: '#374151'
                                  }}>
                                    <strong>Fields:</strong>
                                    <div style={{ marginTop: '8px' }}>
                                      {metaobject.fields.map((field, fieldIndex) => (
                                        <div key={field.key || fieldIndex} style={{ 
                                          marginBottom: '12px', 
                                          padding: '8px', 
                                          background: '#f8f9fa', 
                                          borderRadius: '4px',
                                          border: '1px solid #e9ecef'
                                        }}>
                                          <div style={{ fontWeight: 600, color: '#495057', marginBottom: '4px' }}>
                                            {field.key}:
                                          </div>
                                          <div style={{ color: '#6c757d' }}>
                                            {renderMetafieldValue(field.value)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ 
                                    background: '#ffffff', 
                                    padding: '12px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #e5e7eb',
                                    fontSize: '14px',
                                    color: '#374151'
                                  }}>
                                    <strong>Value:</strong>
                                    <div style={{ marginTop: '8px' }}>
                                      {renderMetafieldValue(metaobject.value)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          </div> {/* End of scrollable content container */}
        </div>
      </div>
    </div>
  );
};

export default PreviewFreeFormContent;
